import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useTrackAgentPillClicked } from '@air/analytics';

import { SaveWorkspaceAgentAssetListAsBoardModal } from '~/components/v2/components/workspace/workspace-agent-asset-list/SaveWorkspaceAgentAssetListAsBoardModal';
import { WorkspaceAgentAssetListArtifactCard } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentAssetListArtifactCard';
import { WorkspaceAgentAssetListPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentAssetListPill';
import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { useOpenWorkspaceAgentAssetList } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentAssetList';
import { useOpenWorkspaceAgentAssetPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentAssetPill';
import { usePrivateWorkspaceAgentAssetListThumbnails } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentAssetListThumbnails';
import { usePrivateWorkspaceAgentHydratedArtifactPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentHydratedArtifactPill';
import { usePrivateWorkspaceAgentPinnedArtifacts } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentPinnedArtifacts';
import { useRegisterWorkspaceAgentAssetPillClipIds } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useRegisterWorkspaceAgentAssetPillClipIds';
import { type AssetListPill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { flattenWorkspaceAgentAssetPillItems } from '~/components/v2/components/workspace/workspace-agent-panel/utils/flattenWorkspaceAgentAssetPillItems';
import { resolveWorkspaceAgentAssetListCardTitle } from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentAssetListCardTitle';
import { isWorkspaceAgentArtifactHydrationPill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentArtifactHydration';
import { useWorkspaceAgentAssetListViewStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentAssetListViewStore';

export type PrivateWorkspaceAgentAssetListPillChipProps = {
  isStreaming: boolean;
  onOpenArtifactCallback?: () => void;
  onShowInThread?: () => void;
  pill: AssetListPill;
  showSaveActions?: boolean;
};

export const PrivateWorkspaceAgentAssetListPillChip = memo(
  ({
    isStreaming,
    onOpenArtifactCallback,
    onShowInThread,
    pill,
    showSaveActions = true,
  }: PrivateWorkspaceAgentAssetListPillChipProps) => {
    const {
      inViewRef,
      isPending,
      isUnavailable,
      pill: hydrated,
    } = usePrivateWorkspaceAgentHydratedArtifactPill({
      isStreaming,
      pill,
    });
    const listPill = hydrated.type === 'assetList' ? hydrated : pill;
    const listCount = listPill.count;
    const displayPills = useMemo(() => [listPill], [listPill]);
    const assetItems = useMemo(() => flattenWorkspaceAgentAssetPillItems(displayPills), [displayPills]);

    useRegisterWorkspaceAgentAssetPillClipIds(displayPills);

    const { thumbnails, unavailableCount } = usePrivateWorkspaceAgentAssetListThumbnails(assetItems, { isStreaming });
    const { openAssetClip } = useOpenWorkspaceAgentAssetPill();
    const { openAssetList } = useOpenWorkspaceAgentAssetList();
    const { trackAgentPillClicked } = useTrackAgentPillClicked();
    const { canPin, isArtifactPinned, sessionId, setArtifactPinned, workspaceId } =
      usePrivateWorkspaceAgentPinnedArtifacts();
    const savedBoard = useWorkspaceAgentAssetListViewStore((state) => state.savedBoardsByArtifactId[pill.id]);
    const markSavedAsBoard = useWorkspaceAgentAssetListViewStore((state) => state.markSavedAsBoard);
    const registerArtifactTitle = useWorkspaceAgentAssetListViewStore((state) => state.registerArtifactTitle);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

    // Only an artifact-backed pill has a page: its id is the artifact id. Legacy item-only pills do not.
    const isArtifactBacked = isWorkspaceAgentArtifactHydrationPill(pill);
    // The citation is only an id. `count: 1` on that placeholder is not the list. Title and the
    // takeover label wait until `Artifacts.Rows.list` has replaced it.
    const hasLoadedList = listPill.items !== undefined;

    const resolvedTitle = useMemo(() => {
      if (!hasLoadedList) {
        return undefined;
      }

      return resolveWorkspaceAgentAssetListCardTitle({
        count: listCount,
        thumbnails,
      });
    }, [hasLoadedList, listCount, thumbnails]);

    useEffect(() => {
      if (!hasLoadedList || !isArtifactBacked || !resolvedTitle) {
        return;
      }

      registerArtifactTitle(pill.id, resolvedTitle);
    }, [hasLoadedList, isArtifactBacked, pill.id, registerArtifactTitle, resolvedTitle]);

    const handleOpenThumbnail = useCallback(
      (id: string) => {
        if (!assetItems.some((item) => item.clipId === id)) {
          return;
        }

        const thumbnail = thumbnails.find((entry) => entry.id === id);
        trackAgentPillClicked({
          object: 'asset_list',
          object_count: assetItems.length,
          object_name: thumbnail?.label ?? 'Selected assets',
        });
        openAssetClip(id);
      },
      [assetItems, openAssetClip, thumbnails, trackAgentPillClicked],
    );

    const handleOpenList = useCallback(() => {
      if (!openAssetList) {
        return;
      }

      trackAgentPillClicked({
        object: 'asset_list',
        object_count: listCount,
        object_name: 'Open list',
      });
      onOpenArtifactCallback?.();
      openAssetList(pill.id, resolvedTitle);
    }, [listCount, onOpenArtifactCallback, openAssetList, pill.id, resolvedTitle, trackAgentPillClicked]);

    const clipIds = useMemo(() => assetItems.flatMap((item) => (item.clipId ? [item.clipId] : [])), [assetItems]);

    const handleSaveAsBoard = useCallback(() => {
      setIsSaveModalOpen(true);
    }, []);

    const handleCloseSaveModal = useCallback(() => {
      setIsSaveModalOpen(false);
    }, []);

    const handleSavedAsBoard = useCallback(
      ({ boardId, boardTitle }: { boardId: string; boardTitle: string }) => {
        markSavedAsBoard(pill.id, { boardId, boardTitle });
      },
      [markSavedAsBoard, pill.id],
    );

    const handlePinToThread = useCallback(() => {
      setArtifactPinned(pill.id, true);
    }, [pill.id, setArtifactPinned]);

    const handleUnpin = useCallback(() => {
      setArtifactPinned(pill.id, false);
    }, [pill.id, setArtifactPinned]);

    const canOpenThumbnails = thumbnails.some((thumbnail) => thumbnail.canOpen);

    if (isPending) {
      return (
        <span className="inline-flex max-w-full items-center align-middle leading-none" ref={inViewRef}>
          <WorkspaceAgentPendingPill label="Assets" />
        </span>
      );
    }

    if (isUnavailable) {
      return (
        <span className="inline-flex max-w-full items-center align-middle leading-none" ref={inViewRef}>
          <WorkspaceAgentUnavailablePill count={listCount} kind="asset" referenceId={listPill.id} />
        </span>
      );
    }

    // Members were named and every one settled missing. A count-only handle has no members to lose.
    if (thumbnails.length > 0 && unavailableCount === thumbnails.length) {
      return <WorkspaceAgentUnavailablePill count={listCount} kind="asset" referenceId={listPill.id} />;
    }

    if (isArtifactBacked) {
      return (
        <>
          <WorkspaceAgentAssetListArtifactCard
            artifactId={pill.id}
            count={listCount}
            isPinned={isArtifactPinned(pill.id)}
            onOpenList={openAssetList ? handleOpenList : undefined}
            onOpenThumbnail={canOpenThumbnails ? handleOpenThumbnail : undefined}
            onPinToThread={canPin ? handlePinToThread : undefined}
            onSaveAsBoard={handleSaveAsBoard}
            onShowInThread={onShowInThread}
            onUnpin={onShowInThread ? handleUnpin : undefined}
            savedBoardTitle={savedBoard?.boardTitle ?? null}
            showSaveActions={showSaveActions}
            thumbnails={thumbnails}
            title={resolvedTitle}
            unavailableCount={unavailableCount}
          />
          {isSaveModalOpen ? (
            <SaveWorkspaceAgentAssetListAsBoardModal
              {...(listCount > clipIds.length ? { artifactId: pill.id, sessionId, workspaceId } : {})}
              clipIds={clipIds}
              onClose={handleCloseSaveModal}
              onSaved={handleSavedAsBoard}
              title={resolvedTitle}
              trackLocation="workspace-agent-panel"
            />
          ) : null}
        </>
      );
    }

    return (
      <WorkspaceAgentAssetListPill
        count={listCount}
        onOpenList={undefined}
        onOpenThumbnail={canOpenThumbnails ? handleOpenThumbnail : undefined}
        thumbnails={thumbnails}
        unavailableCount={unavailableCount}
      />
    );
  },
);

PrivateWorkspaceAgentAssetListPillChip.displayName = 'PrivateWorkspaceAgentAssetListPillChip';
